/**
 * AdminClientiSearchBar — F-012 Phase 3 (IMPL-P3-004).
 *
 * Debounced (300ms) free-text search over the business-account list. The
 * debounce uses native `setTimeout` (no lodash), matching the
 * `CFInput`/`IBANInput`/`ArchivioSearchBar` pattern in the codebase.
 *
 * Controlled API: the parent owns the committed `value` and receives the
 * debounced `onChange`. `aria-label` provides the accessible name (no visible
 * <label>).
 */
import { useEffect, useRef, useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface AdminClientiSearchBarProps {
  /** Committed search value (parent state). */
  value: string;
  /** Debounced change handler. */
  onChange: (value: string) => void;
  /** Visual hint that a network request is in flight. */
  isSearching?: boolean;
  /** Debounce delay in ms. Default 300ms. */
  debounceMs?: number;
}

export function AdminClientiSearchBar({
  value,
  onChange,
  isSearching = false,
  debounceMs = 300,
}: AdminClientiSearchBarProps) {
  const [local, setLocal] = useState(value);

  // Keep local in sync if the upstream value changes externally (e.g. clear).
  useEffect(() => {
    setLocal(value);
  }, [value]);

  // Debounce: only emit after the user stops typing for `debounceMs`. Skip the
  // initial render where `local === value` to avoid a spurious emit on mount.
  const lastEmittedRef = useRef(value);
  useEffect(() => {
    if (local === lastEmittedRef.current) return;
    const t = setTimeout(() => {
      lastEmittedRef.current = local;
      onChange(local);
    }, debounceMs);
    return () => clearTimeout(t);
  }, [local, debounceMs, onChange]);

  const handleClear = () => {
    setLocal('');
    lastEmittedRef.current = '';
    onChange('');
  };

  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        type="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder="Cerca per nome o email…"
        aria-label="Cerca clienti"
        className="pl-9 pr-20"
      />
      <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
        {isSearching ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
        ) : null}
        {local ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleClear}
            aria-label="Cancella ricerca"
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
